import { VariantProps, cva } from 'class-variance-authority';
import { DetailedHTMLProps, ForwardedRef, TextareaHTMLAttributes, forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

const textareaVariants = cva(
    [
        'flex items-center',
        'w-full',
        'font-medium',
        'bg-transparent border border-grayscale-300 ',
        'placeholder:text-grayscale-400',
        'focus:border-primary focus:outline focus:outline-2 focus:outline-primary/10',
    ],
    {
        variants: {
            size: {
                sm: ['text-sm py-[7px] px-4 rounded-md'],
                normal: ['text-sm py-[7px] px-4 rounded-md'],
                lg: ['text-base py-2 px-[18px] rounded-md'],
            },
            invalid: {
                true: ['border-danger-500 outline outline-danger-50 focus:border-danger-500 focus:outline-danger-50'],
                false: [],
            },
            valid: {
                true: [
                    'border-success-500 outline outline-success-50 focus:border-success-500 focus:outline-success-50',
                ],
                false: [],
            },
            disabled: {
                true: ['bg-[#FAFAFA] dark:bg-[#20262D] opacity-70'],
                false: [],
            },
        },
        defaultVariants: {
            size: 'normal',
            disabled: false,
            invalid: false,
            valid: false,
        },
    }
);

export interface TextareaProps
    extends Omit<DetailedHTMLProps<TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement>, 'size'>,
        VariantProps<typeof textareaVariants> {
    ref?: ForwardedRef<HTMLTextAreaElement>;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>((props, ref) => {
    const { size, invalid, valid, disabled, className, ...rest } = props;

    return (
        <textarea
            {...rest}
            ref={ref}
            disabled={disabled}
            className={twMerge(textareaVariants({ size, invalid, valid, disabled, className }))}
        />
    );
});

export default Textarea;
